import { useAtomValue, useSetAtom } from "jotai";
import { useRouter } from "next/router";
import type { SetStateAction } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { graphql, requestSubscription, useRelayEnvironment } from "react-relay";
import type { Disposable } from "relay-runtime";

import { notEmpty } from "shared/lib/utils";

import { appAtom } from "scmp-app/lib/app/atoms";
import type { hooksSearchResultStreamSubscription } from "scmp-app/queries/__generated__/hooksSearchResultStreamSubscription.graphql";

import { searchResultStreamAtom } from "./atoms";
import { generateStreamId } from "./helpers";

export const useDefaultSearchInputSate = ((initValue?: (() => string) | string) => {
  const { defaultSearchString, defaultSearchStringLoaded } = useAtomValue(appAtom);

  const isDirtyReference = useRef(false);
  const [value, setValue] = useState(initValue ?? "");

  useEffect(() => {
    if (
      isDirtyReference.current ||
      defaultSearchStringLoaded !== true ||
      !notEmpty(defaultSearchString) ||
      notEmpty(value)
    ) {
      return;
    }
    setValue(defaultSearchString);
  }, [defaultSearchString, defaultSearchStringLoaded, value]);

  const changeValue = useCallback((next: SetStateAction<string>) => {
    isDirtyReference.current = true;
    setValue(next);
  }, []);

  return [value, changeValue];
}) as typeof useState<string>;

export const useSearchResultStreamSubscription = () => {
  const { query } = useRouter();
  const environment = useRelayEnvironment();
  const subscriptionReference = useRef<Disposable | undefined>(undefined);

  const streamId = useMemo(() => generateStreamId(), []);
  const setSearchResultStream = useSetAtom(searchResultStreamAtom);

  useEffect(() => {
    if (!notEmpty(query.q)) return;

    setSearchResultStream({
      exploreKeyAngles: [],
      followTheTimeline: [],
      isReceivingExploreKeyAngles: true,
      isReceivingFollowTheTimeline: true,
      mode: undefined,
    });

    subscriptionReference.current = requestSubscription<hooksSearchResultStreamSubscription>(
      environment,
      {
        onNext: response => {
          if (!notEmpty(response?.searchResultStream)) return;

          if (notEmpty(response.searchResultStream.mode)) {
            setSearchResultStream(current => ({
              ...current,
              mode: response.searchResultStream.mode,
            }));
            return;
          }
          if (response.searchResultStream.done === true) {
            setSearchResultStream(current => ({
              ...current,
              isReceivingExploreKeyAngles: false,
              isReceivingFollowTheTimeline: false,
            }));
            subscriptionReference.current?.dispose();
            return;
          }
          if (response.searchResultStream.categoryDone) {
            setSearchResultStream(current => ({
              ...current,
              isReceivingExploreKeyAngles: false,
            }));
            return;
          }
          if (response.searchResultStream.timelineDone) {
            setSearchResultStream(current => ({
              ...current,
              isReceivingFollowTheTimeline: false,
            }));
            return;
          }
          switch (response.searchResultStream.__typename) {
            case "ArticleSearchResultStreamItemCategory":
              setSearchResultStream(current => ({
                ...current,
                exploreKeyAngles: [...current.exploreKeyAngles, response],
                mode: current.mode ?? "NAVIGATOR",
              }));
              break;
            case "ArticleSearchResultStreamItemTimelineItem":
            case "ArticleSearchResultStreamItemTimelineSummary":
              setSearchResultStream(current => ({
                ...current,
                followTheTimeline: [...current.followTheTimeline, response],
                mode: current.mode ?? "TIMELINE",
              }));
          }
        },
        subscription: graphql`
          subscription hooksSearchResultStreamSubscription($streamId: String!) {
            searchResultStream(filter: { streamId: $streamId }) {
              __typename
              ... on ArticleSearchResultStreamMode {
                mode
              }
              ... on ArticleSearchResultStreamDone {
                done
              }
              ... on ArticleSearchResultStreamItemCategoryDone {
                categoryDone: done
              }
              ... on ArticleSearchResultStreamItemTimelineDone {
                timelineDone: done
              }
              ...searchExploreKeyAnglesArticleSearchResultStreamItemCategory
              ...searchFollowTheTimelineArticleSearchResultStreamItem
            }
          }
        `,
        variables: { streamId },
      },
    );

    return () => {
      subscriptionReference.current?.dispose();
    };
  }, [environment, query.q, setSearchResultStream, streamId]);

  return {
    streamId,
  };
};
